import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { Icon, Spacing } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import checkboxRenderer from './CheckboxRenderer';
import InsuranceModal from './InsuranceModal';
import { formatCurrency } from '../utils/currency';
import insuranceETN from '../images/brands-unique/etn/insuranceETN.png';
import 'styles/components/CardCheckbox';
import useCurrencyConfig from '../hooks/whitelabel/useCurrencyConfig';

/**
 * Passenger insurance checkbox component
 * @param {Object} props - Component props
 * @param {string} props.passenger - Passenger identifier
 * @param {number} props.insuranceOutgoingUnitAmount - Cost of outgoing insurance
 * @param {number} props.insuranceIncomingUnitAmount - Cost of incoming insurance
 * @param {boolean} props.disabled - Whether the checkbox is disabled
 * @returns JSX Element
 */
const PassengerInsuranceCheckbox = ({
  passenger,
  insuranceOutgoingUnitAmount,
  insuranceIncomingUnitAmount,
  disabled,
}) => {
  const { t } = useTranslation('purchase');
  const { currency, locale } = useCurrencyConfig();

  return (
    <div className="card-checkbox">
      <Icon type={insuranceETN} hasMargin />
      <div className="card-checkbox-wrapper">
        <Spacing size="S" vertical>
          <InsuranceModal />
          {insuranceOutgoingUnitAmount > 0 && (
            <Field
              id={`${passenger}.wantsOutgoingInsurance`}
              name={`${passenger}.wantsOutgoingInsurance`}
              type="checkbox"
              label={t('label.add_departing_trip_insurance', {
                cost: formatCurrency(insuranceOutgoingUnitAmount, 2, currency, locale),
              })}
              component={checkboxRenderer}
              disabled={disabled}
            />
          )}
          {insuranceIncomingUnitAmount > 0 && (
            <Field
              id={`${passenger}.wantsIncomingInsurance`}
              name={`${passenger}.wantsIncomingInsurance`}
              type="checkbox"
              label={t('label.add_return_trip_insurance', {
                cost: formatCurrency(insuranceIncomingUnitAmount, 2, currency, locale),
              })}
              component={checkboxRenderer}
              disabled={disabled}
            />
          )}
        </Spacing>
      </div>
    </div>
  );
};

PassengerInsuranceCheckbox.propTypes = {
  passenger: PropTypes.string.isRequired,
  insuranceOutgoingUnitAmount: PropTypes.number.isRequired,
  insuranceIncomingUnitAmount: PropTypes.number,
  disabled: PropTypes.bool,
};

export default PassengerInsuranceCheckbox;
